import type { SubjectType } from "../containers/Esign/types/esign.types.ts"

const RoutesPaths = {
  home: "/home",
  // The recipient's page. Public, outside the app shell, and the one path the
  // startup guard must leave alone — its token arrives in the URL fragment.
  signing: "/sign",
  // A customer's upload link from a meeting invitation. Public, outside the app
  // shell, token in the fragment — the startup guard must leave it alone too.
  customerUpload: "/upload",
  // Public, like signing. The backend's share-link serializer builds this path.
  sharedFile: "/files/shared",
  projectEducation: "/p/project-education",
  dashboard: "/dashboard",
  invoices: "/invoices",
  signatureRequests: "/signature-requests",
  map: "/map",
  customers: {
    list: "/customers",
    view: (id: string) => `/customers/${id}`
  },
  subcontractors: {
    list: "/subcontractors",
    // `tab` opens the profile on one of its four tabs; `document` names a
    // compliance document type so an expiry alert can land on the row whose
    // Upload/Replace action resolves it, rather than on the profile's default
    // tab with the recipient left to hunt for which document lapsed.
    view: (id: string, params?: Partial<Record<"tab" | "document", string>>) =>
      buildUrl(`/subcontractors/${id}`, params)
  },
  suppliers: {
    list: "/suppliers",
    view: (id: string) => `/suppliers/${id}`
  },
  document_reviews: {
    list: "/document_reviews",
    view: (id: string) => `/document_reviews/${id}`
  },
  files: {
    list: (params?: Partial<Record<"tab" | "fileId" | "filter", string>>) =>
      buildUrl("/files", params)
  },
  team_management: "/team_management",
  team_performance_member: (id: string) => `/team_management/performance/${id}`,
  account_deletion_requests: "/account_deletion_requests",
  reports: {
    list: "/reports",
    create: "/reports/new",
    createResume: (id: string) => `/reports/new/${id}`,
    view: (id: string) => `/reports/${id}`,
    edit: (id: string) => `/reports/${id}/edit`
  },
  //
  auth: {
    login: "/login",
    addEmail: "/add-email",
    whatsapp_login: "/whatsapp_login"
  },
  // NEW
  communication: {
    root: "/communication",
    overview: "/communication/overview",
    messages: "/communication/messages",
    campaigns: "/communication/campaigns",
    optOuts: "/communication/opt-outs",
    automations: "/communication/automations",
    settings: "/communication/settings",
    settingsEmail: "/communication/settings/email",
    settingsSendingRules: "/communication/settings/sending-rules",
    settingsSms: "/communication/settings/sms",
    settingsTemplates: "/communication/settings/templates"
  },
  settings: {
    profile: "/settings",
    configuration: "/settings/configuration",
    company: "/settings/company",
    roles: "/settings/roles",
    templates: "/settings/templates",
    projectTemplates: "/settings/templates/project",
    projectTemplate: (id: string) => `/settings/templates/project/${id}`,
    documentTemplates: "/settings/templates/documents",
    permissions: "/settings/permissions",
    dashboardTemplates: "/settings/templates/dashboard",
    lists: "/settings/templates/lists",
    leadSourceBudget: "/settings/templates/lead-source-budget",
    emailTemplates: "/settings/templates/email",
    formFieldRequirements: "/settings/form-field-requirements",
    notifications: "/settings/notifications",
    branches: "/settings/branches",
    branch: (id: string) => `/settings/branches/${id}`,
    smsUsage: "/settings/sms-usage",
    automation: "/settings/automation",
    automationNew: "/settings/automation/new",
    automationEdit: (id: string) => `/settings/automation/${id}`
  },
  lead: {
    list: "/leads",
    listInactive: "/leads/inactive",
    listArchivedLegacy: "/leads/archived",
    add: "/leads/add",
    view: (id: string, params?: Partial<Record<"tab", string>>) =>
      buildUrl(`/leads/${id}`, params),
    financial: (id: string) => `/leads/${id}/financial`,
    legacyEdit: (id: string) => `/leads/${id}/edit`
  },
  archive: {
    list: "/archive",
    leads: "/archive?tab=leads",
    projects: "/archive?tab=projects"
  },
  project: {
    grid: "/projects/",
    list: "/projects/list",
    add: "/projects/add",
    view: (id: string) => ({
      _layout: `/projects/${id}`,
      overview: `/projects/${id}`,
      milestones: `/projects/${id}/milestones`,
      commissions: `/projects/${id}/commissions`,
      finance: `/projects/${id}/finance`,
      overviewTab: `/projects/${id}/overview`,
      invoices: `/projects/${id}/invoices`,
      files: (params?: Partial<Record<"nav" | "fileId", string>>) =>
        buildUrl(`/projects/${id}/files`, params),
      messages: (
        params?: Partial<Record<"conversation" | "message", string>>
      ) => buildUrl(`/projects/${id}/messages`, params),
      tasks: `/projects/${id}/tasks`,
      logs: `/projects/${id}/logs`,
      communication: `/projects/${id}/communication`,
      /** @deprecated replaced by `files({ fileId })` — kept so old links redirect */
      allFiles: `/projects/${id}/files/albums/all-files`,
      album: (albumId: string) => `/projects/${id}/files/albums/${albumId}`,
      /** @deprecated replaced by `files` — kept so old links redirect */
      albums: `/projects/${id}/albums`,
      /** @deprecated replaced by `album` — kept so old links redirect */
      legacyAlbum: (albumId: string) => `/projects/${id}/albums/${albumId}`,
      /** @deprecated replaced by `files` — kept so old links redirect */
      documents: `/projects/${id}/documents`
    }),
    edit: (id: string) => `/projects/${id}/edit`
  },
  proposal: {
    list: "/proposals",
    view: (id: string) => `/proposals/${id}`,
    edit: (id: string) => `/proposals/${id}/edit`,

    add: Object.assign(
      (leadId: string) => `/proposals/add${`?leadID=${leadId}`}`,
      {
        toString: () => "/proposals/add"
      }
    )
  },
  contract: {
    list: "/contracts",
    view: (id: string) => `/contracts/${id}`,
    edit: (id: string) => `/contracts/${id}/edit`,

    add: Object.assign(
      (leadId: string, proposalId: string) =>
        `/contracts/add?${`leadID=${leadId}&proposalID=${proposalId}`}`,
      {
        toString: () => "/contracts/add"
      }
    )
  },
  documentTemplates: {
    add: "/document_templates/add",
    edit: (id: string) => `/document_templates/${id}/edit`
  },
  proposalDocument: {
    create: (templateId: string, leadId: string) =>
      `/proposal_templates/${templateId}/create/${leadId}`,
    edit: (id: string) => `/proposal_templates/${id}/edit`
  },
  contractDocument: {
    create: {
      path: `/contract_templates/:leadId/create`,
      url: (
        leadId: string,
        params?: Partial<
          Record<"templateId" | "proposalId" | "informationSource", string>
        >
      ) => buildUrl(`/contract_templates/${leadId}/create`, params)
    },
    edit: (id: string) => `/contract_templates/${id}/edit`
  },
  guide: {
    home: "/guide",
    story: "/guide/story",
    academy: "/guide/academy",
    module: (moduleSlug: string) => `/guide/academy/${moduleSlug}`,
    lesson: (lessonId: string) => `/guide/lessons/${lessonId}`,
    qa: "/guide/qa",
    // Static, isolated preview screens the Product Tour routes to instead of
    // a real Lead/Project when the account has none — never a real record,
    // never a real API call, so a brand-new account still sees "what a Lead/
    // Project looks like" rather than an honest-but-useless empty fallback.
    tourPreview: {
      lead: "/guide/tour-preview/lead",
      project: "/guide/tour-preview/project",
      projectMilestones: "/guide/tour-preview/project/milestones",
      projectFinance: "/guide/tour-preview/project/finance",
      projectInvoices: "/guide/tour-preview/project/invoices",
      projectFiles: "/guide/tour-preview/project/files",
      projectActivity: "/guide/tour-preview/project/activity"
    }
  },
  portal: {
    home: "/portal/home",
    projects: "/portal/projects",
    // A project's own workspace — every tab is a real, deep-linkable route
    // scoped by :id, never a hidden Redux selection. `project(id)` alone
    // lands on Overview; `project(id, "timeline")` etc. for the others.
    project: (id: string, tab?: string) =>
      `/portal/projects/${id}${tab ? `/${tab}` : ""}`,
    projectPattern: "/portal/projects/:id/:tab?",
    documents: "/portal/documents",
    photos: "/portal/photos",
    messages: "/portal/messages",
    payments: "/portal/payments",
    calendar: "/portal/calendar",
    account: "/portal/account",
    settings: "/portal/settings"
  },
  esign: {
    newEnvelopePath: "/esign/envelopes/new",
    newEnvelope: (params: {
      lead?: string
      /** `company` raises a standalone request on a Company Files document. */
      scope?: "company"
      subject_type?: SubjectType
      subject_id?: string
    }) => buildUrl("/esign/envelopes/new", params),
    // Where the "your turn to countersign" email lands.
    signPath: "/esign/envelopes/:id/sign",
    sign: (id: string) => `/esign/envelopes/${id}/sign`,
    // The read-only tracking page a sent request opens on.
    requestPath: "/esign/requests/:id",
    request: (id: string) => `/esign/requests/${id}`,
    envelopePath: "/esign/envelopes/:id",
    envelope: (
      id: string,
      params?: Partial<Record<"step" | "field" | "recipient", string>>
    ) => buildUrl(`/esign/envelopes/${id}`, params)
  }
}

function buildUrl(base: string, params?: Record<string, string | undefined>) {
  if (!params) return base
  const query = new URLSearchParams(
    Object.entries(params).filter(
      (entry): entry is [string, string] => entry[1] !== undefined
    )
  ).toString()
  return query ? `${base}?${query}` : base
}

export { RoutesPaths }
